import React, {useState} from "react";
import {
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    Typography,
    TableBody,
    Box,
    IconButton,
    Collapse,
    Button,
} from "@mui/material";
import {Create, KeyboardArrowDown, KeyboardArrowRight} from "@mui/icons-material";
import {Trans, useTranslation} from "react-i18next";
import dayjs from "dayjs";
import {Notification, NotificationStatus} from "../../../types/domain/notification";
import DOMPurify from "dompurify";
import {useLoadableValueOrDefault} from "../../../hooks/jotaiHooks";
import {UserPreferences} from "../../../types/domain/user";
import {authenticatedUserPreferencesLoadable} from "../../../store/authenticatedUserStore";
import {DEFAULT_USER_PREFERENCES} from "../../../utils/userUtils";

interface AnnouncementsTableProps {
    announcements: Notification[];
    total: number;
    page: number;
    rowsPerPage: number;
    onPageChange?: (newPage: number) => void;
    onRowsPerPageChange?: (newSize: number) => void;
    editAnnouncement: (notification: Notification) => void;
    setAnnouncements: (notifications: Notification[]) => void;
    changeNotificationStatus: (id: string, status: NotificationStatus) => Promise<void>;
}

export const AnnouncementsTable: React.FC<AnnouncementsTableProps> = ({
    announcements,
    page,
    rowsPerPage,
    editAnnouncement,
}) => {
    const {t} = useTranslation();
    const [openRowId, setOpenRowId] = useState<string | null>(null);
    const userPreferences = useLoadableValueOrDefault<UserPreferences>(
        authenticatedUserPreferencesLoadable,
        DEFAULT_USER_PREFERENCES,
    );

    const toggleRow = (id: string) => {
        setOpenRowId((prev) => (prev === id ? null : id));
    };

    return announcements?.length ? (
        <>
            <TableContainer sx={{overflowY: "auto", backgroundColor: "white"}}>
                <Table>
                    <TableHead sx={{backgroundColor: "#F3F5FA"}}>
                        <TableRow>
                            <TableCell />
                            <TableCell>
                                <Typography fontWeight="bold">#</Typography>
                            </TableCell>
                            <TableCell>
                                <Typography fontWeight="bold">{t("announcements_page.title")}</Typography>
                            </TableCell>
                            <TableCell>
                                <Typography fontWeight="bold">{t("announcements_page.platforms")}</Typography>
                            </TableCell>
                            <TableCell>
                                <Typography fontWeight="bold">{t("announcements_page.startTime")}</Typography>
                            </TableCell>
                            <TableCell>
                                <Typography fontWeight="bold">{t("announcements_page.viewed")}</Typography>
                            </TableCell>
                            <TableCell>
                                <Typography fontWeight="bold">{t("announcements_page.status")}</Typography>
                            </TableCell>
                            <TableCell>
                                <Typography fontWeight="bold">{t("announcements_page.actions")}</Typography>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {announcements?.map((announcement, index) => {
                            const isOpen = openRowId === announcement.id;
                            return (
                                <React.Fragment key={announcement.id}>
                                    <TableRow>
                                        <TableCell>
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    if (announcement?.id) {
                                                        toggleRow(announcement.id);
                                                    }
                                                }}
                                            >
                                                {isOpen ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                                        <TableCell>{announcement.name}</TableCell>
                                        <TableCell>{announcement.targetPlatforms?.join(", ")}</TableCell>
                                        <TableCell sx={{whiteSpace: "nowrap"}}>
                                            {announcement.status === NotificationStatus.SCHEDULED ||
                                            announcement.status === NotificationStatus.SENT
                                                ? dayjs(announcement.startTime).format("MMMM D, YYYY h:mm A")
                                                : ""}
                                        </TableCell>
                                        <TableCell>{announcement.seen ?? "0"}</TableCell>
                                        <TableCell>{announcement.status}</TableCell>

                                        <TableCell>
                                            <Button
                                                onClick={() => {
                                                    editAnnouncement(announcement);
                                                }}
                                                size="small"
                                                variant="outlined"
                                                startIcon={<Create />}
                                                disabled={
                                                    announcement.status === NotificationStatus.SENT ||
                                                    announcement.status === NotificationStatus.SCHEDULED ||
                                                    announcement.status === NotificationStatus.CANCELLED
                                                }
                                            >
                                                {t("announcements_page.edit")}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow sx={{backgroundColor: "#F3F5FA"}}>
                                        <TableCell colSpan={8} sx={{paddingBottom: 0, paddingTop: 0}}>
                                            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                                <Box
                                                    sx={{
                                                        backgroundColor: "white",
                                                        borderRadius: "4px",
                                                        padding: "1.5rem",
                                                        border: "1px solid #e0e0e0",
                                                    }}
                                                    margin={2}
                                                    dangerouslySetInnerHTML={{
                                                        __html: `<div>
                                                        <div style="border-radius: 4px;border: 1px solid lightgrey;padding: 14px 8px 8px 16px;margin-bottom:15px;">
                                                        <div style="font-weight: 700;margin-bottom: 4px">${DOMPurify.sanitize(
                                                            announcement.metaData?.content?.[
                                                                userPreferences.language ?? "en"
                                                            ]?.title ?? "",
                                                        )}</div>
                                                            <div style="padding-bottom: 4px;margin-bottom:1px solid lightgrey;">${DOMPurify.sanitize(
                                                                announcement.metaData?.content?.[
                                                                    userPreferences.language ?? "en"
                                                                ]?.shortText ?? "",
                                                            )}</div>
                                                        </div>
                                                        <div style="border-radius: 4px;border: 1px solid lightgrey;padding: 14px 8px 8px 16px;padding: 16px;">
                                                            ${DOMPurify.sanitize(
                                                                announcement.metaData?.content?.[
                                                                    userPreferences.language ?? "en"
                                                                ]?.content ?? "",
                                                            )}</div>
                                                        </div>`,
                                                    }}
                                                />
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    ) : (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            sx={{marginTop: "2rem"}}
        >
            <Typography variant="h3" color="textSecondary">
                <Trans
                    i18nKey="announcements_page.no_announcements_message"
                    components={{
                        button: (
                            <Typography
                                component="span"
                                sx={{
                                    color: "primary.main",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                    fontSize: "1.1rem",
                                }}
                            />
                        ),
                    }}
                />
            </Typography>
        </Box>
    );
};
